//=============================================================================================
// 																	SCENE UNIFORM PARAMS

import { DatGuiAddVec3 } from "../core/gui";
import { ConvertHexColorToVec3, Float, FloatTypeArr, Mat4, Vector3 } from "../core/types";
import { MathLerp } from "../core/utils";
import { SceneBase } from "./scene";

//=============================================================================================
export class SceneUniformBufferParameters {
    //=============================================Members Start
    CameraPosition = new Vector3(0.0, 0, -1.3);
    CameraZoom = new Float(5.0);
    //
    PointLightPos1 = new Vector3(-0.23, 0.85, -1.5);
    PointLightRadius1 = new Float(6.5);
    //
    PointLightPos2 = new Vector3(2.0, 0.85, -4.0);
    UniformRoughness = new Float(0.3);
    //
    LightIntensity1 = new Float(5.0);
    AmbientLight = new Float(0.1);
    ScreenRatio = new Float(0.1);
    Time = new Float(0.1);
    //
    LightColor = new Vector3(1.0, 1.0, 1.0);
    DeltaTime = new Float(0.1);
    //
    WorldToProjectorSpaceMat = new Mat4();
    //
    ProjectorPos = new Vector3(0.0, 0.0, -16.45);
    ProjectorZoom = new Float(3.0);
    //
    ProjectorTargetPos = new Vector3(0.0, 0.0, 0.0);
    UniformMetalness = new Float(0.0);
    //
    ProjectorColor = new Vector3(1.0, 1.0, 1.0);
    ProjectorIntensity = new Float(2.0);
    //
    OccluderPlaneCenter = new Vector3(0.0, 0.0, 0.0); //Main Painting Object Pos
    ProjectorIntensityCurve = new Float(1.0);
    //
    OccluderPlaneExtent = new Vector3(1.0, 1.0, 0.0);
    FresnelMin = new Float(0.04);
    //
    SHCoef = new Vector3(1.0, 1.0, 1.0);
    PhiOffset = new Float(0.0);
    //
    SceneBoundsCenter = new Vector3(0.0, 0.0, 0.0);
    ThetaOffset = new Float(0.0);
    //
    SceneBoundsExtent = new Vector3(1.0, 1.0, 1.0);
    NumLightprobes3D = new Float(0.0);
    //
    FresnelMax = new Float(1.0); //F90
    ProjectorXYRatio = new Float(1.375);
    ProjectorIntensityVolume = new Float(3.0);
    ProjectorFalloffVolume = new Float(10.0);
    //
    EmmissiveColor = new Vector3(1.0, 1.0, 1.0);
    SpotlightIntensity = new Float(1.0);
    //
    SpotlightPos = new Vector3(0.0, -0.85, -8.0);
    SpotlightOuterAngle = new Float(0.3);
    //
    SpotlightTargetPos = new Vector3(0, 0, -1);
    SpotlightInnerAngleOffset = new Float(0.2);
    //
    SDFMeshBoundsCenter = new Vector3(0, 0, -4);
    SDFMeshUniformScale = new Float(0.2);
    //
    SDFMeshBoundsExtent = new Vector3(1.5, 1.5, 1.5);
    SwitchRopeSphereRadius = new Float(0.035);
    //
    LightSwitchHandlePos = new Vector3(1.5, 1.5, 1.5);
    SwitchRopeNumSpheres = new Float(0.035);
    //
    SpotlightPos2 = new Vector3(-2.3, MathLerp(0.5, 2.5, Math.random()), MathLerp(-6.5, -3.5, Math.random())); //Set in LightSwitchOnUpdateAdditional
    EnvMapRotationAngle = new Float(4.95);
    //
    SpotlightTargetPos2 = new Vector3(3.5, -1.5, -4.5);
    UniformSpecularScale = new Float(1.0);
    //
    SDFMeshBoundsCenter2 = new Vector3(0, 0, -4);
    SDFMeshUniformScale2 = new Float(0.2);
    //
    SDFMeshBoundsExtent2 = new Vector3(1.5, 1.5, 1.5);
    LampFlickerScale = new Float(1.0);
    //
    SpotlightPos3 = new Vector3(-4.8, 4.25, -0.4);
    LampLightRadius = new Float(9.0);
    //
    LightColor2 = new Vector3(0.8, 0.8, 0.8);
    LampLightIntensity = new Float(0.75);
    //
    SDFHelperCenter = new Vector3(-1.04, -0.11, -2.17);
    SDFHelperFloat1 = new Float(0.4);
    //
    SDFHelperExtent = new Vector3(-1.04, -0.11, -2.3);
    SDFHelperFloat2 = new Float(1.0);
    //
    UserInputPosNDC = new Vector3(-1.04, -0.11, -2.3);
    LeftLampScale = new Float(1.0);
    //
    IntersectionPosWS = new Vector3(-1.04, -0.11, -2.3);
    PhoneGhostEffectParam = new Float(0.0);
    //
    PyramidParams = new Vector3(0.0, 0.0, 1.0); //x: twist y:spiral z:hallway start light intensity
    PyramidBrightness = new Float(0.2);

    //=============================================Members End

    GenerateMembersArr(): FloatTypeArr[] {
        const values = Object.values(this).filter((value) => value instanceof FloatTypeArr);
        return values;
    }

    GenerateParamsDeclarations(): string {
        const names = Object.getOwnPropertyNames(this);
        const values = Object.values(this).filter((value) => value instanceof FloatTypeArr);
        let declaration = ``;
        const size = values.length;
        for (let i = 0; i < size; i++) {
            const param = values[i];
            const name = names[i];
            let type = `float`;
            if (param instanceof Vector3) {
                type = `vec3`;
            }
            if (param instanceof Mat4) {
                type = `mat4`;
            }
            declaration += type;
            declaration += ` `;
            declaration += name;
            declaration += `;`;
            if (i != size - 1) {
                declaration += `\n`;
            }
        }
        return declaration;
    }
}

export function SubmitSceneUniformBufferParamsDebugUI(folder: dat.GUI) {
    const buffer = SceneBase.UniformBuffer.Params;

    const paramsFolder = folder.addFolder("SceneUniformParams");
    paramsFolder.open();

    //DatGuiAddVec3(paramsFolder, buffer.SHCoef, `SHCoef`, 0, Math.PI, true);
    //
    {
        const lightsFolder = paramsFolder.addFolder("Lights");
        //lightsFolder.open();

        //PointLight1
        lightsFolder
            .add(buffer.EnvMapRotationAngle, "x", 0, Math.PI * 2.0)
            .name("EnvMapRotationAngle")
            .step(0.01)
            .listen();
        lightsFolder.add(SceneBase, "PointerLightDepth", 0, 20).name("Cursor Light Z").step(0.01);
        DatGuiAddVec3(lightsFolder, buffer.SpotlightPos, `FlashlightPos`, -20, 20, true);
        DatGuiAddVec3(lightsFolder, buffer.SpotlightPos2, `SpotLightPos2`, -20, 20, true);
        DatGuiAddVec3(lightsFolder, buffer.SpotlightPos3, `SpotLightPos3`, -20, 20, true);
        DatGuiAddVec3(lightsFolder, buffer.SpotlightTargetPos2, `SpotlightTargetPos2`, -20, 20, true, true);
        DatGuiAddVec3(lightsFolder, buffer.PointLightPos1, `LightPos1`, -15, 15, true);
        DatGuiAddVec3(lightsFolder, buffer.PointLightPos2, `LightPos2`, -10, 5, true);
        lightsFolder
            .add(buffer.SpotlightOuterAngle, "x", 0, Math.PI / 2)
            .name("SpotlightAngleOut")
            .step(0.01);
        lightsFolder
            .add(buffer.SpotlightInnerAngleOffset, "x", 0, Math.PI / 2)
            .name("SpotlightAngleIn")
            .step(0.01);

        lightsFolder.addColor(SceneBase, "LightColorHex").onChange(function (hexColor) {
            //ConvertHexColorToVec3(hexColor, buffer.LightColor);
            //ConvertHexColorToVec3(hexColor, buffer.LightColor2);
            ConvertHexColorToVec3(hexColor, buffer.EmmissiveColor);
        });
        lightsFolder.addColor(SceneBase, "PaintingColorHex").onChange(function (hexColor) {
            //ConvertHexColorToVec3(hexColor, buffer.EmmissiveColor);
            //ConvertHexColorToVec3(hexColor, buffer.LightColor2);
            ConvertHexColorToVec3(hexColor, buffer.LightColor);
        });
        lightsFolder.add(buffer.PointLightRadius1, "x", 0, 20).name("LightRadius1").step(0.01).listen();
        lightsFolder.add(buffer.LampLightRadius, "x", 0, 20).name("LampLightRadius").step(0.01).listen();
        lightsFolder.add(buffer.LightIntensity1, "x", 0, 50).name("LightIntensity1").step(0.01).listen();
        lightsFolder.add(buffer.LampLightIntensity, "x", 0, 5).name("Lamp Light Intensity").step(0.01).listen();
        lightsFolder.add(buffer.AmbientLight, "x", -1, 50).name("AmbientLight").step(0.01).listen();
        lightsFolder.add(buffer.PhoneGhostEffectParam, "x", 0, 1).name("GhostEffect").step(0.01).listen();
    }

    {
        const projFolder = paramsFolder.addFolder("Projector");
        //projFolder.open();

        DatGuiAddVec3(projFolder, buffer.ProjectorPos, `ProjectorPos`, -40, 20, true);
        DatGuiAddVec3(projFolder, buffer.ProjectorTargetPos, `ProjectorTargetPos`, -5, 5, true);
        projFolder.add(buffer.ProjectorZoom, "x", 0, 10).name("ProjectorZoom").step(0.01).listen();
        projFolder.addColor(SceneBase, "ProjectorColorHex").onChange(function (hexColor) {
            ConvertHexColorToVec3(hexColor, buffer.ProjectorColor);
        });
        projFolder.add(buffer.ProjectorIntensity, "x", 0, 15).name("ProjectorIntensity").step(0.01).listen();
        //projFolder.add(buffer.ProjectorIntensityCurve, "x", 0, 5).name("ProjectorCurve").step(0.01).listen();
        projFolder.add(buffer.ProjectorIntensityVolume, "x", 0, 5).name("ProjectorIntensityVolume").step(0.01).listen();
        projFolder.add(buffer.ProjectorFalloffVolume, "x", 0, 20).name("ProjectorFalloffVolume").step(0.01).listen();
        projFolder.add(buffer.ProjectorXYRatio, "x", 0, 3).name("ProjectorXYRatio").step(0.01).listen();
    }

    {
        const matFolder = paramsFolder.addFolder("Material");
        //matFolder.open();
        matFolder.add(buffer.UniformSpecularScale, "x", 0, 1).name("UniformSpecularScale").step(0.01).listen();
        matFolder.add(buffer.UniformRoughness, "x", 0, 1).name("Roughness").step(0.01).listen();
        matFolder.add(buffer.UniformMetalness, "x", 0, 1).name("Metalness").step(0.01).listen();
        matFolder.add(buffer.FresnelMin, "x", 0, 1).name("FresnelMin(F0)").step(0.01).listen();
        matFolder.add(buffer.FresnelMax, "x", 0, 1).name("FresnelMax(F90)").step(0.01).listen();
    }
    {
        const sdfFoler = paramsFolder.addFolder("SDF Helper");
        //sdfFoler.open();

        DatGuiAddVec3(sdfFoler, buffer.SDFHelperCenter, `Center`, -10, 10, false);
        DatGuiAddVec3(sdfFoler, buffer.SDFHelperExtent, `Extent`, -10, 10, false);
        sdfFoler.add(buffer.SDFHelperFloat1, "x", -10, 10).name("Float1").step(0.01).listen();
        sdfFoler.add(buffer.SDFHelperFloat2, "x", -10, 10).name("Float2").step(0.01).listen();
        sdfFoler.add(buffer.PyramidBrightness, "x", 0, 10).name("PyrBright").step(0.01).listen();
    }
}
